import classNames from "classnames";
import { useRef } from "react";
import { CanvasTagId, CanvasTemplateMinimal, CanvasTemplateTag } from "shared/datamodel/schemas/canvas-template";
import style from "../templates-modal.module.css";
import type { TemplatesSection, CoverType } from "../templates-modal";
import { CloseButton } from "./legacy/close-button";
import { TemplateGalleryView } from "./legacy/template-gallery-view";
import { TemplatePreviewView } from "./shared/template-preview-view";
import { TemplateGallery } from "./reorder/template-gallery/template-gallery-v2";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";

interface TemplatesModalUIProps {
  // Data props
  sections: TemplatesSection[];
  tags: CanvasTemplateTag[];
  searchTerm: string;
  selectedTag: { id: CanvasTagId; name: string };
  previewTemplate: CanvasTemplateMinimal | null;
  coverType: CoverType | null;
  showUpgradeCTA?: boolean;
  isCimReorderEnabled: boolean;
  syncedTemplatesSectionId: number;
  shouldShowSyncedOnTop: boolean;
  accountName: string;

  // Event handlers
  onSearch: (searchTerm: string) => void;
  onTagClicked: (tagId: CanvasTagId, tagName: string) => void;
  onTemplateClick: (template: CanvasTemplateMinimal) => void;
  onPreviewTemplate: (template: CanvasTemplateMinimal) => void;
  onSeeAllClick: (tagId: number, tagName: string) => void;
  onUpgradeCTAClick: (source: string) => void;
  onDismiss: () => void;
  onClosePreview: () => void;
  onChooseTemplateFromPreview: (template: CanvasTemplateMinimal) => void;
  onUpgradeModalShown: () => void;
  onEditTemplate: (template: CanvasTemplateMinimal) => void;
  onDeleteTemplate: (templateId: string) => void;
  onCreateTemplate?: () => void;
}

export function TemplatesModalUI({
  sections,
  tags,
  searchTerm,
  selectedTag,
  previewTemplate,
  coverType,
  showUpgradeCTA,
  isCimReorderEnabled,
  syncedTemplatesSectionId,
  shouldShowSyncedOnTop,
  accountName,
  onSearch,
  onTagClicked,
  onTemplateClick,
  onPreviewTemplate,
  onSeeAllClick,
  onUpgradeCTAClick,
  onDismiss,
  onClosePreview,
  onChooseTemplateFromPreview,
  onUpgradeModalShown,
  onEditTemplate,
  onDeleteTemplate,
  onCreateTemplate,
}: TemplatesModalUIProps) {
  const modalRef = useRef(null);
  const newTemplateGallery = useFeatureFlag("cim-new-template-gallery");
  // Show preview mode if template is selected
  if (previewTemplate) {
    return (
      <TemplatePreviewView
        template={previewTemplate}
        onClosePreview={onClosePreview}
        onChooseTemplate={onChooseTemplateFromPreview}
        onUpgradeModalShown={onUpgradeModalShown}
        onDismiss={onDismiss}
      />
    );
  }

  if (isCimReorderEnabled && newTemplateGallery) {
    return (
      <div
        className={classNames(style.container, style.containerCIMFooterEnabled, style.unsetWidth)}
        ref={modalRef}
        data-testid="template-library-modal"
        // this creates a nice grid to inspect layout - excellent for debug
        // style={{
        //   backgroundImage:
        //     "linear-gradient(to right, rgba(255,0,0,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,0,0,0.1)1px, transparent 1px)",
        //   backgroundSize: "8px 8px",
        // }}
      >
        <TemplateGallery
          sections={sections}
          tags={tags}
          searchTerm={searchTerm}
          selectedTag={selectedTag}
          coverType={coverType}
          syncedTemplatesSectionId={syncedTemplatesSectionId}
          shouldShowSyncedOnTop={shouldShowSyncedOnTop}
          accountName={accountName}
          onSearch={onSearch}
          onTagClicked={onTagClicked}
          onTemplateClick={onTemplateClick}
          onPreviewTemplate={onPreviewTemplate}
          onDismiss={onDismiss}
          onSeeAllClick={onSeeAllClick}
          onEditTemplate={onEditTemplate}
          onDeleteTemplate={onDeleteTemplate}
          onCreateTemplate={onCreateTemplate}
          showUpgradeCTA={showUpgradeCTA}
          onUpgradeCTAClick={onUpgradeCTAClick}
        />
      </div>
    );
  }
  // Show main gallery view
  return (
    <div
      className={classNames(style.container, isCimReorderEnabled && style.containerCIMFooterEnabled)}
      ref={modalRef}
      data-testid="template-library-modal"
    >
      <CloseButton onDismiss={onDismiss} />
      <TemplateGalleryView
        sections={sections}
        tags={tags}
        searchTerm={searchTerm}
        selectedTag={selectedTag}
        coverType={coverType}
        showUpgradeCTA={showUpgradeCTA}
        isCimReorderEnabled={isCimReorderEnabled}
        syncedTemplatesSectionId={syncedTemplatesSectionId}
        shouldShowSyncedOnTop={shouldShowSyncedOnTop}
        accountName={accountName}
        onSearch={onSearch}
        onTagClicked={onTagClicked}
        onTemplateClick={onTemplateClick}
        onPreviewTemplate={onPreviewTemplate}
        onSeeAllClick={onSeeAllClick}
        onUpgradeCTAClick={onUpgradeCTAClick}
        onEditTemplate={onEditTemplate}
        onDeleteTemplate={onDeleteTemplate}
        onCreateTemplate={onCreateTemplate}
      />
    </div>
  );
}
